"use client"

import { jwtDecode } from "jwt-decode"
import { AlertCircle, CheckCircle, CreditCard, MapPin, Shield, User } from "lucide-react"
import type React from "react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { api } from "../services/api"
import "./KYC.css"

const KYC: React.FC = () => {
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [taxId, setTaxId] = useState("")
  const [street, setStreet] = useState("")
  const [neighborhood, setNeighborhood] = useState("")
  const [state, setState] = useState("")
  const [zipCode, setZipCode] = useState("")
  const [idType, setIdType] = useState("INE")
  const [idNumber, setIdNumber] = useState("")
  const [isNotified, setIsNotified] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const navigate = useNavigate()

  // Check if the user is already logged in and has an accepted KYC status
  useEffect(() => {
    const token = localStorage.getItem("authToken")

    if (token) {
      try {
        // Decode the JWT token
        const decoded: any = jwtDecode(token)

        // If the KYC status is accepted, redirect to the main page
        if (decoded.kycStatus === "accepted") {
          navigate("/")
        }
      } catch (error) {
        console.error("Error decoding token:", error)
      }
    } else {
      // If no token found, redirect to the auth page
      navigate("/auth")
    }
  }, [navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    try {
      const token = localStorage.getItem("authToken")
      if (token) {
        // Set the Authorization header for this request
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`
      }
      const response = await api.post("/kyc", {
        firstName,
        lastName,
        phoneNumber,
        taxId,
        street,
        neighborhood,
        state,
        zipCode,
        idType,
        idNumber,
        isNotified,
      })

      // If a new JWT is returned (on KYC approval), store it
      if (response.data.token) {
        localStorage.setItem("authToken", response.data.token) // Replace the old JWT
        // Update axios headers for future requests
        api.defaults.headers.common["Authorization"] = `Bearer ${response.data.token}`

        // Decode the JWT to access user info
        const decoded: any = jwtDecode(response.data.token)

        // Set the JWT in axios headers for future requests
        api.defaults.headers.common["Authorization"] = `Bearer ${response.data.token}`

        // Navigate based on whether the user is new or existing
        // Redirect based on kycStatus
        if (decoded.kycStatus === "pending") {
          navigate("/kyc")
        } else if (decoded.kycStatus === "accepted") {
          navigate("/")
        } else {
          // Handle other statuses if necessary
          navigate("/")
        }
      } else {
        throw new Error("Token not found in the response.")
      }
    } catch (error: any) {
      console.error("Error submitting KYC:", error)
      setError(error.response?.data?.message || "Error submitting KYC. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="kyc-page">
      <div className="kyc-container">
        <div className="kyc-header">
          <div className="kyc-header-icon">
            <Shield className="header-icon" />
          </div>
          <h1>Verificación de Identidad</h1>
          <p>Completa tu perfil para acceder a nuestros servicios de Rampa</p>
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle className="error-icon" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="kyc-form">
          {/* Personal Information Section */}
          <div className="form-section">
            <div className="section-header">
              <User className="section-icon" />
              <h2>Información Personal</h2>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Nombres *</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Ingresa tu nombre"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Apellidos *</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Ingresa tus apellidos"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Número de teléfono *</label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="Ej: +52 55 1234 5678"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">RFC *</label>
                <input
                  type="text"
                  value={taxId}
                  onChange={(e) => setTaxId(e.target.value.toUpperCase())}
                  placeholder="Ej: ABCD123456EFG"
                  className="form-input"
                  maxLength={13}
                  required
                />
              </div>
            </div>
          </div>

          {/* Address Section */}
          <div className="form-section">
            <div className="section-header">
              <MapPin className="section-icon" />
              <h2>Dirección</h2>
            </div>

            <div className="form-grid">
              <div className="form-group full-width">
                <label className="form-label">Calle y número *</label>
                <input
                  type="text"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  placeholder="Ej: Av. Reforma 123, Col. Centro"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Colonia *</label>
                <input
                  type="text"
                  value={neighborhood}
                  onChange={(e) => setNeighborhood(e.target.value)}
                  placeholder="Ingresa tu colonia"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Estado *</label>
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="Ej: Ciudad de México"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Código Postal *</label>
                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  placeholder="Ej: 06000"
                  className="form-input"
                  maxLength={5}
                  pattern="[0-9]{5}"
                  required
                />
              </div>
            </div>
          </div>

          {/* Identification Section */}
          <div className="form-section">
            <div className="section-header">
              <CreditCard className="section-icon" />
              <h2>Identificación Oficial</h2>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Tipo de identificación *</label>
                <select value={idType} onChange={(e) => setIdType(e.target.value)} className="form-select" required>
                  <option value="INE">INE (Credencial para Votar)</option>
                  <option value="Passport">Pasaporte</option>
                  <option value="Driver License">Licencia de Conducir</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Número de documento *</label>
                <input
                  type="text"
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                  placeholder="Ingresa el número de tu documento"
                  className="form-input"
                  required
                />
              </div>
            </div>
          </div>

          {/* Notification Checkbox */}
          <div className="form-section">
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="notifications"
                checked={isNotified}
                onChange={(e) => setIsNotified(e.target.checked)}
                className="form-checkbox"
              />
              <label htmlFor="notifications" className="checkbox-label">
                <CheckCircle className="checkbox-icon" />
                Quiero recibir notificaciones sobre el estado de mis transacciones
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className={`kyc-submit-btn ${isSubmitting ? "submitting" : ""}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="loading-spinner"></div>
                Verificando...
              </>
            ) : (
              <>
                <Shield className="btn-icon" />
                Verificar Identidad
              </>
            )}
          </button>
        </form>

        <div className="kyc-footer">
          <p>
            <Shield className="footer-icon" />
            Tu información está protegida con encriptación de nivel bancario
          </p>
        </div>
      </div>
    </div>
  )
}

export default KYC
